// D&D 5e rules shared by the sheet UI (display) and chat (attribute resolution).
import type { Char } from "./types";

export const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"] as const;
export type Ability = (typeof ABILITIES)[number];
export const ABILITY_NAMES: Record<Ability, string> = {
  str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma",
};

export const SKILLS: [string, string, Ability][] = [
  ["acrobatics", "Acrobatics", "dex"],
  ["animal_handling", "Animal Handling", "wis"],
  ["arcana", "Arcana", "int"],
  ["athletics", "Athletics", "str"],
  ["deception", "Deception", "cha"],
  ["history", "History", "int"],
  ["insight", "Insight", "wis"],
  ["intimidation", "Intimidation", "cha"],
  ["investigation", "Investigation", "int"],
  ["medicine", "Medicine", "wis"],
  ["nature", "Nature", "int"],
  ["perception", "Perception", "wis"],
  ["performance", "Performance", "cha"],
  ["persuasion", "Persuasion", "cha"],
  ["religion", "Religion", "int"],
  ["sleight_of_hand", "Sleight of Hand", "dex"],
  ["stealth", "Stealth", "dex"],
  ["survival", "Survival", "wis"],
];

export const CONDITIONS = [
  "blinded", "charmed", "deafened", "exhaustion", "frightened", "grappled", "incapacitated", "invisible",
  "paralyzed", "petrified", "poisoned", "prone", "restrained", "stunned", "unconscious",
];

const n = (v: any, d = 0) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : d;
};
export const mod = (score: number) => Math.floor((score - 10) / 2);
export const signed = (v: number) => (v >= 0 ? `+${v}` : `${v}`);

export function crToPb(cr: string): number {
  const c = cr.includes("/") ? 0 : n(cr, 0);
  return 2 + Math.floor(Math.max(0, c - 1) / 4);
}

/** Fill in derived attributes (mods, saves, skills, pb, spell dc…) on top of stored ones. */
export function computed5e(ch: Pick<Char, "attrs">): Record<string, string> {
  const a = ch.attrs || {};
  const out: Record<string, string> = {};
  const npc = a.npc === "1";
  const level = Math.max(1, n(a.level, 1));
  const pb = a.pb_override ? n(a.pb_override, 2) : npc ? crToPb(a.cr || "0") : 2 + Math.floor((level - 1) / 4);
  out.pb = String(pb);
  const jack = a.jack_of_all_trades === "1" ? Math.floor(pb / 2) : 0;
  for (const ab of ABILITIES) {
    const m = mod(n(a[ab], 10));
    out[`${ab}_mod`] = String(m);
    const prof = a[`${ab}_save_prof`] === "1";
    out[`${ab}_save`] = String(m + (prof ? pb : 0) + n(a[`${ab}_save_bonus`]) + n(a.global_save_bonus));
  }
  for (const [key, , ab] of SKILLS) {
    const lvl = n(a[`skill_${key}`], 0); // 0 none, 1 proficient, 2 expertise
    const m = n(out[`${ab}_mod`]);
    const bonus = lvl >= 2 ? pb * 2 : lvl === 1 ? pb : jack;
    out[key] = String(m + bonus + n(a[`skill_${key}_bonus`]));
  }
  out.passive_perception = String(10 + n(out.perception) + n(a.passive_bonus));
  out.passive_investigation = String(10 + n(out.investigation));
  out.passive_insight = String(10 + n(out.insight));
  out.initiative = String(n(out.dex_mod) + n(a.init_bonus) + (a.init_jack === "1" ? jack : 0));
  const sa = (a.spell_ability || "none") as Ability | "none";
  if (sa !== "none") {
    const sm = n(out[`${sa}_mod`]);
    out.spell_mod = String(sm);
    out.spell_dc = String(8 + pb + sm + n(a.spell_dc_bonus));
    out.spell_attack = String(pb + sm + n(a.spell_attack_bonus));
  } else {
    out.spell_mod = "0";
    out.spell_dc = "0";
    out.spell_attack = "0";
  }
  out.carry_capacity = String(n(a.str, 10) * 15);
  return out;
}

/** All attributes (stored + computed) plus the Roll20-style "_max" access. */
export function allAttrs(ch: Char, sheet: string): Record<string, string> {
  const base = { ...(ch.attrs || {}) };
  if (sheet === "dnd5e") Object.assign(base, computed5e(ch), filterSet(ch.attrs));
  base.character_name = ch.name;
  base.character_id = ch.id;
  return base;
}
// stored values win over computed ones only when explicitly set non-empty
function filterSet(a: Record<string, string>) {
  const o: Record<string, string> = {};
  for (const [k, v] of Object.entries(a || {})) if (v !== "" && v != null && !COMPUTED_KEYS.has(k)) o[k] = v;
  return o;
}
const COMPUTED_KEYS = new Set<string>([
  "pb", ...ABILITIES.flatMap(a => [`${a}_mod`, `${a}_save`]), ...SKILLS.map(s => s[0]),
  "passive_perception", "passive_investigation", "passive_insight", "initiative", "spell_mod", "spell_dc", "spell_attack", "carry_capacity",
]);

// ---- roll strings the sheet emits (expanded on the client) ----
export type RollMode = "normal" | "adv" | "dis" | "both";

export function d20(mode: RollMode, bonus: string): string[] {
  const b = bonus ? `+${bonus}` : "";
  if (mode === "adv") return [`2d20kh1${b}`];
  if (mode === "dis") return [`2d20kl1${b}`];
  if (mode === "both") return [`1d20${b}`, `1d20${b}`];
  return [`1d20${b}`];
}

export function checkRoll(title: string, subtitle: string, bonusExpr: string, mode: RollMode, tracker = false): string {
  const rolls = d20(mode, bonusExpr);
  const tr = tracker ? " &{tracker}" : "";
  const rows = rolls.map((r, i) => `{{${rolls.length > 1 ? (i ? "r2" : "r1") : "Result"}=[[${r}${i === 0 ? tr : ""}]]}}`).join(" ");
  return `&{template:5e} {{name=${title}}} {{subtitle=${subtitle}}} ${rows} {{character=@{character_name}}}`;
}

export function attackRoll(atk: Record<string, string>, mode: RollMode): string {
  const ab = (atk.ability || "str") as Ability | "none" | "spell";
  const abMod = ab === "none" ? "0" : ab === "spell" ? "@{spell_mod}" : `@{${ab}_mod}`;
  const hit = atk.hit_override?.trim()
    ? atk.hit_override.trim()
    : `${abMod}${atk.prof !== "0" ? "+@{pb}" : ""}${atk.bonus ? `+${atk.bonus}` : ""}`;
  const rolls = d20(mode, hit);
  const parts = [`&{template:5e} {{name=${atk.name || "Attack"}}} {{subtitle=@{character_name}}}`];
  if (atk.no_attack !== "1") rolls.forEach((r, i) => parts.push(`{{${rolls.length > 1 ? (i ? "r2" : "r1") : "Attack"}=[[${r}]]}}`));
  if (atk.damage?.trim()) {
    const dm = atk.dmg_mod === "0" || atk.hit_override?.trim() ? "" : `+${abMod}`;
    parts.push(`{{Damage=[[${atk.damage.trim()}${dm}]] ${atk.dmg_type || ""}}}`);
    parts.push(`{{Crit Extra=[[${critDice(atk.damage.trim())}]]}}`);
  }
  if (atk.damage2?.trim()) parts.push(`{{Damage 2=[[${atk.damage2.trim()}]] ${atk.dmg_type2 || ""}}}`);
  if (atk.save?.trim()) parts.push(`{{Save=${atk.save}}}`);
  if (atk.range?.trim()) parts.push(`{{Range=${atk.range}}}`);
  if (atk.notes?.trim()) parts.push(`{{desc=${atk.notes.trim()}}}`);
  return parts.join(" ");
}

/** Dice-only part of a damage expression, for the extra crit roll. */
export function critDice(expr: string): string {
  const dice = expr.match(/\d*d\d+/gi);
  return dice ? dice.join("+") : "0";
}

export function spellCard(sp: Record<string, string>, mode: RollMode): string {
  const lvl = sp.level === "0" || !sp.level ? "Cantrip" : `Level ${sp.level}`;
  const parts = [`&{template:5e} {{name=${sp.name || "Spell"}}} {{subtitle=${lvl}${sp.school ? ` ${sp.school}` : ""}}}`];
  if (sp.cast_time) parts.push(`{{Casting Time=${sp.cast_time}}}`);
  if (sp.range) parts.push(`{{Range=${sp.range}}}`);
  if (sp.components) parts.push(`{{Components=${sp.components}}}`);
  if (sp.duration) parts.push(`{{Duration=${sp.duration}}}`);
  if (sp.attack === "attack") d20(mode, "@{spell_attack}").forEach((r, i, arr) => parts.push(`{{${arr.length > 1 ? (i ? "r2" : "r1") : "Attack"}=[[${r}]]}}`));
  if (sp.attack === "save") parts.push(`{{Save=DC @{spell_dc} ${(sp.save_ability || "").toUpperCase()}}}`);
  if (sp.damage) parts.push(`{{Damage=[[${sp.damage}]] ${sp.dmg_type || ""}}}`);
  if (sp.heal) parts.push(`{{Healing=[[${sp.heal}]]}}`);
  if (sp.desc) parts.push(`{{desc=${sp.desc.slice(0, 1500)}}}`);
  return parts.join(" ");
}

export function blank5e(npc = false): Record<string, string> {
  const a: Record<string, string> = {
    level: "1", str: "10", dex: "10", con: "10", int: "10", wis: "10", cha: "10",
    ac: "10", speed: "30 ft", hp: "10", hp_max: "10", hp_temp: "", hit_dice: "1d8", spell_ability: "none",
  };
  if (npc) Object.assign(a, { npc: "1", cr: "1", type: "Medium humanoid", xp: "200" });
  return a;
}
